import { useState, useEffect, useRef } from 'react';
import { apiService } from '@/services/api';
import { useTranslation } from 'react-i18next';
import { VehicleIdentityCard } from '@/components/vehicles/VehicleIdentityCard';
import { normalizeVehicleIdentity, normalizeVehicleIdentityList, type VehicleIdentityData } from '@/components/vehicles/vehicleIdentity';
import { VehicleSearchInput } from '@/components/vehicles/VehicleSearchInput';

interface VehicleMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  disabled?: boolean;
}

export function VehicleMultiSelect({ value, onChange, className, disabled }: VehicleMultiSelectProps) {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<VehicleIdentityData[]>([]);
  const [vehicleMap, setVehicleMap] = useState<Record<string, VehicleIdentityData>>({});
  const [loading, setLoading] = useState(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (search.length < 2) {
      setVehicles([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiService.getVehicles({
          page: 1,
          limit: 10,
          search,
          status: 'in_stock',
        });
        if (data && Array.isArray(data.vehicles)) {
          const normalizedVehicles = normalizeVehicleIdentityList(data.vehicles);
          setVehicles(normalizedVehicles);
          setVehicleMap((prev) => {
            const next = { ...prev };
            normalizedVehicles.forEach((vehicle) => {
              next[vehicle.id] = vehicle;
            });
            return next;
          });
          setShowSuggestions(true);
        } else {
          setVehicles([]);
        }
      } catch (error) {
        console.error('Failed to fetch vehicles:', error);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadVehiclesBatch = async () => {
      if (value.length === 0) return;
      const missingIds = value.filter((id) => !vehicleMap[id]);
      if (missingIds.length === 0) return;

      try {
        setBootstrapLoading(true);
        const data = await apiService.getVehicles({
          page: 1,
          limit: 200,
          status: 'in_stock',
        });
        if (!data || !Array.isArray(data.vehicles)) return;
        const normalizedVehicles = normalizeVehicleIdentityList(data.vehicles);
        setVehicleMap((prev) => {
          const next = { ...prev };
          normalizedVehicles.forEach((vehicle) => {
            next[vehicle.id] = vehicle;
          });
          return next;
        });
      } catch (error) {
        console.error('Failed to bootstrap vehicles list:', error);
      } finally {
        setBootstrapLoading(false);
      }
    };
    void loadVehiclesBatch();
  }, [value, vehicleMap]);

  const selectedVehicles = value
    .map((id) => vehicleMap[id])
    .filter((vehicle): vehicle is VehicleIdentityData => Boolean(vehicle));

  const handleAdd = (vehicle: VehicleIdentityData) => {
    if (!value.includes(vehicle.id)) {
      onChange([...value, vehicle.id]);
    }
    setSearch('');
    setVehicles([]);
    setShowSuggestions(false);
  };

  const handleRemove = (vehicleId: string) => {
    onChange(value.filter(v => v !== vehicleId));
  };

  return (
    <div className={className}>
      {selectedVehicles.length > 0 && (
        <div className="flex flex-col gap-2 mb-2">
          {selectedVehicles.map((vehicle) => (
            <VehicleIdentityCard
              key={vehicle.id}
              vehicle={vehicle}
              variant="selected"
              onRemove={() => handleRemove(vehicle.id)}
              removeDisabled={disabled}
              removeTitle={t('leads.autoTransfer.removeVehicle')}
            />
          ))}
        </div>
      )}
      {bootstrapLoading && selectedVehicles.length === 0 && value.length > 0 && (
        <p className="text-xs text-muted-foreground mb-2">{t('common.loading')}...</p>
      )}
      <div ref={wrapperRef} className="relative">
        <VehicleSearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('leads.autoTransfer.searchVehicles')}
          disabled={disabled}
          isLoading={loading}
          onFocus={() => {
            if (vehicles.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />

        {showSuggestions && vehicles.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {!loading && vehicles.map((vehicle) => (
              <button
                key={vehicle.id}
                type="button"
                onClick={() => handleAdd(vehicle)}
                disabled={value.includes(vehicle.id)}
                className="w-full px-4 py-3 text-left hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <VehicleIdentityCard vehicle={vehicle} />
              </button>
            ))}
          </div>
        )}

        {showSuggestions && !loading && search.length >= 2 && vehicles.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg">
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('common.noResults')}
            </div>
          </div>
        )}
      </div>

      {search.length > 0 && search.length < 2 && (
        <p className="text-xs text-muted-foreground mt-1">
          {t('common.typeAtLeastChars', { count: 2 })}
        </p>
      )}
    </div>
  );
}
